import { TicketPriorityEnum } from './ticket'

export const SCORING_PROFILE_KEYS = {
  ENTITY_HEALTH: 'entity.health.scoring',
  CONTACT: 'contact.scoring',
  DEAL: 'deal.scoring',
} as const

export type ScoringProfileKey =
  (typeof SCORING_PROFILE_KEYS)[keyof typeof SCORING_PROFILE_KEYS]

export interface ScoreThresholds {
  healthy: number
  watch: number
}

/**
 * Ticket priority strings the entity-health engine accepts as "high
 * priority". The DB column is a free varchar, so this is the ticket priority
 * enum plus the legacy `CRITICAL` value the engine has always honored.
 */
export const ENTITY_HEALTH_TICKET_PRIORITY_LEVELS: readonly string[] = [
  ...Object.values(TicketPriorityEnum),
  'CRITICAL',
]

export interface EntityHealthScoringProfile {
  enabled: boolean
  axisWeights: {
    relationship: number
    delivery: number
    commercial: number
  }
  thresholds: ScoreThresholds
  minimumSamples: {
    relationship: number
    delivery: number
    commercial: number
  }
  coldStartDefaults: {
    relationship: number
    delivery: number
    commercial: number
  }
  relationshipRules: {
    /**
     * How many trailing days count as "recent engagement" when scoring the
     * relationship axis. Defaults to 90; clamped to >= 7.
     */
    recencyWindowDays: number
    /**
     * T33 — per-channel base weight for an interaction's engagement value. A
     * held meeting is worth more than an email; tickets are intentionally absent
     * (Delivery-only signal). Weighted engagement is scored against the account's
     * own average, so a steady channel mix stays on baseline and only a *shift*
     * (toward richer or thinner channels) moves the score. Each clamped to >= 0.
     */
    channelWeights: {
      meeting: number
      call: number
      email: number
      message: number
      thread: number
    }
    /**
     * T33 — maximum points deducted when recent engagement is one-way broadcast
     * (no inbound replies, no held meetings). Ramps from 0 (at/above
     * `healthyReciprocityRatio` two-way share) to this cap (at zero two-way).
     * Bounded so a silent-but-not-dead account lands in "watch", not "risk".
     */
    oneWayPenalty: number
    /**
     * T33 — the share of recent interactions that must be two-way
     * (client-initiated inbound, held meetings, recordings) for no one-way
     * penalty to apply. Clamped to (0, 1].
     */
    healthyReciprocityRatio: number
  }
  deliveryRules: {
    overdueProjectPenalty: number
    openTicketPenalty: number
    highPriorityTicketPenalty: number
    /**
     * Ticket priority strings treated as high priority by the delivery axis.
     * Validated against ENTITY_HEALTH_TICKET_PRIORITY_LEVELS; falls back to
     * the default set when empty or unrecognized.
     */
    highPriorityLevels: string[]
  }
  commercialRules: {
    targetMarginPercent: number
    targetCollectionRatio: number
    unpaidInvoicePenalty: number
    /**
     * B8/T26 — maximum points the commercial axis can lose to unbilled scope
     * creep (unbilled change-order delta as a share of contract value). Capped
     * so one large absorbed change order can't zero the axis on its own.
     */
    scopeCreepPenaltyCap: number
    /**
     * B8/T26 — the unbilled-creep ratio (unbilled delta / contract baseline
     * value) at which the penalty reaches its cap. The deduction ramps linearly
     * from 0 up to `scopeCreepPenaltyCap` as the ratio climbs to this value.
     */
    scopeCreepFullPenaltyRatio: number
  }
  /**
   * Policy thresholds for health triage tiers and risk alerts. These are the
   * dollar/score gates an org tunes to its book of business — the scoring
   * model internals (score cutoffs, plan-delta bands) stay fixed.
   */
  triageRules: {
    /** Value at risk that (with a low score) makes a client critical. */
    criticalValueAtRisk: number
    /** Value at risk that (with a low score) makes a client high priority. */
    highValueAtRisk: number
    /** Value at risk that alone keeps a client at least medium priority. */
    mediumValueAtRisk: number
    /** Value-at-risk gate written into the client health alert rule. */
    alertValueAtRisk: number
    /** Relationship score below which a high-priority client opens an alert. */
    alertRelationshipScore: number
  }
}

export interface DealScoringProfile {
  enabled: boolean
  thresholds: {
    strong: number
    watch: number
  }
  issueImpacts: {
    missingOwner: number
    missingClient: number
    missingPrimaryContact: number
    missingAmount: number
    missingProbability: number
    pastCloseDate: number
    nearCloseOutsideNegotiation: number
    staleActivity: number
    quietActivity: number
    onHold: number
    // Engagement signals (Gong-style, from the Communication timeline):
    // no inbound/outbound client touch inside contactSilenceDays, and
    // single-threading (all recent communications involve ≤1 distinct contact).
    noRecentClientContact: number
    singleThreaded: number
  }
  staleDays: {
    quiet: number
    stale: number
  }
  nearCloseDays: number
  /** Days without any client communication before noRecentClientContact fires. */
  contactSilenceDays: number
  probabilitySource: 'manual' | 'stage' | 'higher' | 'lower'
  /**
   * T9 (D4) — cold-start sample floor for derived win-probability. A canonical
   * stage whose closed-deal cohort N is below this is treated as noise: the
   * derived number is withheld (the deal falls back to rep confidence / the
   * stage default) and flagged cold-start, rather than presenting a meaningless
   * win-rate with false authority. Per-tenant configurable; defaults to ~35.
   */
  derivedProbabilitySampleFloor: number
  /**
   * T10 (Lever 2) — which probability drives the HEADLINE revenue forecast.
   * `rep` (default, the historical behavior) weights expected deal revenue by
   * the rep's own confidence (`Deal.probability`); `derived` weights it by the
   * tenant's historical win-rate for the deal's canonical stage
   * (`Deal.derivedProbability`, T6), falling back to rep confidence wherever the
   * derived number is withheld (cold-start below the T9 floor). The non-chosen
   * number stays surfaced alongside so the gap remains visible — the two are
   * never averaged.
   */
  headlineProbabilityDriver: 'derived' | 'rep'
}

export const DEFAULT_ENTITY_HEALTH_SCORING_PROFILE: EntityHealthScoringProfile =
  {
    enabled: true,
    axisWeights: {
      relationship: 1,
      delivery: 1,
      commercial: 1,
    },
    thresholds: {
      healthy: 75,
      watch: 50,
    },
    minimumSamples: {
      relationship: 4,
      delivery: 3,
      commercial: 3,
    },
    coldStartDefaults: {
      relationship: 55,
      delivery: 65,
      commercial: 65,
    },
    relationshipRules: {
      recencyWindowDays: 90,
      channelWeights: {
        meeting: 3,
        call: 2.5,
        email: 1,
        message: 0.8,
        thread: 0.7,
      },
      oneWayPenalty: 12,
      healthyReciprocityRatio: 0.25,
    },
    deliveryRules: {
      overdueProjectPenalty: 10,
      openTicketPenalty: 2,
      highPriorityTicketPenalty: 8,
      highPriorityLevels: ['HIGH', 'URGENT', 'CRITICAL'],
    },
    commercialRules: {
      targetMarginPercent: 20,
      targetCollectionRatio: 0.8,
      unpaidInvoicePenalty: 2,
      // Moderate default: up to a 15-point deduction, reached when unbilled
      // scope creep hits 25% of contract value.
      scopeCreepPenaltyCap: 15,
      scopeCreepFullPenaltyRatio: 0.25,
    },
    triageRules: {
      criticalValueAtRisk: 50000,
      highValueAtRisk: 10000,
      mediumValueAtRisk: 5000,
      alertValueAtRisk: 10000,
      alertRelationshipScore: 50,
    },
  }

export const DEFAULT_DEAL_SCORING_PROFILE: DealScoringProfile = {
  enabled: true,
  thresholds: {
    strong: 80,
    watch: 55,
  },
  issueImpacts: {
    missingOwner: 16,
    missingClient: 12,
    missingPrimaryContact: 10,
    missingAmount: 12,
    missingProbability: 8,
    pastCloseDate: 18,
    nearCloseOutsideNegotiation: 10,
    staleActivity: 14,
    quietActivity: 6,
    onHold: 18,
    noRecentClientContact: 10,
    singleThreaded: 6,
  },
  staleDays: {
    quiet: 10,
    stale: 21,
  },
  nearCloseDays: 14,
  contactSilenceDays: 14,
  probabilitySource: 'manual',
  // ~35 closed deals/stage (D4). Mirrors the service-level hard fallback
  // DEFAULT_DERIVED_PROBABILITY_SAMPLE_FLOOR used when no preference is set.
  derivedProbabilitySampleFloor: 35,
  // T10 — default to rep confidence so existing forecasts are unchanged until a
  // tenant opts into the derived (historical win-rate) headline.
  headlineProbabilityDriver: 'rep',
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const numberValue = (value: unknown, fallback: number): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

const integerValue = (value: unknown, fallback: number): number =>
  Math.max(0, Math.round(numberValue(value, fallback)))

const thresholdValue = (value: unknown, fallback: number): number =>
  Math.max(0, Math.min(100, integerValue(value, fallback)))

const objectValue = <T extends object>(value: unknown): Partial<T> =>
  isRecord(value) ? (value as Partial<T>) : {}

const boundedNumber = (
  value: unknown,
  fallback: number,
  min: number,
  max: number,
): number => Math.max(min, Math.min(max, numberValue(value, fallback)))

const nonNegativeNumber = (value: unknown, fallback: number): number =>
  Math.max(0, numberValue(value, fallback))

/**
 * Normalizes the configured high-priority ticket levels: strings only,
 * uppercased, deduped, and validated against the known priority values.
 * Anything degenerate (missing, wrong type, or nothing recognized) falls back
 * to the default set so the delivery axis never loses its signal.
 */
const highPriorityLevelsValue = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [
      ...DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.deliveryRules.highPriorityLevels,
    ]
  }
  const known = new Set(ENTITY_HEALTH_TICKET_PRIORITY_LEVELS)
  const levels = Array.from(
    new Set(
      value
        .filter((level): level is string => typeof level === 'string')
        .map(level => level.trim().toUpperCase())
        .filter(level => known.has(level)),
    ),
  )
  return levels.length > 0
    ? levels
    : [
        ...DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.deliveryRules
          .highPriorityLevels,
      ]
}

export function normalizeEntityHealthScoringProfile(
  value: unknown,
): EntityHealthScoringProfile {
  const input = objectValue<EntityHealthScoringProfile>(value)
  const weights = objectValue<EntityHealthScoringProfile['axisWeights']>(
    input.axisWeights,
  )
  const thresholds = objectValue<EntityHealthScoringProfile['thresholds']>(
    input.thresholds,
  )
  const samples = objectValue<EntityHealthScoringProfile['minimumSamples']>(
    input.minimumSamples,
  )
  const coldStart = objectValue<
    EntityHealthScoringProfile['coldStartDefaults']
  >(input.coldStartDefaults)
  const relationshipRules = objectValue<
    EntityHealthScoringProfile['relationshipRules']
  >(input.relationshipRules)
  const deliveryRules = objectValue<
    EntityHealthScoringProfile['deliveryRules']
  >(input.deliveryRules)
  const commercialRules = objectValue<
    EntityHealthScoringProfile['commercialRules']
  >(input.commercialRules)
  const triageRules = objectValue<EntityHealthScoringProfile['triageRules']>(
    input.triageRules,
  )

  // Axis weights are relative (normalized when the axes combine), so each is
  // clamped to >= 0; an all-zero set is degenerate and falls back to 1/1/1.
  const axisWeights = {
    relationship: nonNegativeNumber(
      weights.relationship,
      DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.axisWeights.relationship,
    ),
    delivery: nonNegativeNumber(
      weights.delivery,
      DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.axisWeights.delivery,
    ),
    commercial: nonNegativeNumber(
      weights.commercial,
      DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.axisWeights.commercial,
    ),
  }
  const totalAxisWeight =
    axisWeights.relationship + axisWeights.delivery + axisWeights.commercial

  const healthyThreshold = thresholdValue(
    thresholds.healthy,
    DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.thresholds.healthy,
  )

  return {
    enabled:
      typeof input.enabled === 'boolean'
        ? input.enabled
        : DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.enabled,
    axisWeights:
      totalAxisWeight > 0
        ? axisWeights
        : { ...DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.axisWeights },
    thresholds: {
      healthy: healthyThreshold,
      // The watch band sits below healthy; a watch threshold above healthy is
      // degenerate and is clamped down to it.
      watch: Math.min(
        healthyThreshold,
        thresholdValue(
          thresholds.watch,
          DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.thresholds.watch,
        ),
      ),
    },
    minimumSamples: {
      relationship: Math.max(
        1,
        integerValue(
          samples.relationship,
          DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.minimumSamples.relationship,
        ),
      ),
      delivery: Math.max(
        1,
        integerValue(
          samples.delivery,
          DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.minimumSamples.delivery,
        ),
      ),
      commercial: Math.max(
        1,
        integerValue(
          samples.commercial,
          DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.minimumSamples.commercial,
        ),
      ),
    },
    coldStartDefaults: {
      relationship: thresholdValue(
        coldStart.relationship,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.coldStartDefaults.relationship,
      ),
      delivery: thresholdValue(
        coldStart.delivery,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.coldStartDefaults.delivery,
      ),
      commercial: thresholdValue(
        coldStart.commercial,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.coldStartDefaults.commercial,
      ),
    },
    relationshipRules: {
      recencyWindowDays: Math.max(
        7,
        integerValue(
          relationshipRules.recencyWindowDays,
          DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.relationshipRules
            .recencyWindowDays,
        ),
      ),
      channelWeights: (() => {
        const defaults =
          DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.relationshipRules.channelWeights
        const weights = objectValue<typeof defaults>(
          relationshipRules.channelWeights,
        )
        return {
          meeting: nonNegativeNumber(weights.meeting, defaults.meeting),
          call: nonNegativeNumber(weights.call, defaults.call),
          email: nonNegativeNumber(weights.email, defaults.email),
          message: nonNegativeNumber(weights.message, defaults.message),
          thread: nonNegativeNumber(weights.thread, defaults.thread),
        }
      })(),
      // Bounded, non-negative point deduction (0 disables the one-way penalty).
      oneWayPenalty: nonNegativeNumber(
        relationshipRules.oneWayPenalty,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.relationshipRules.oneWayPenalty,
      ),
      // A share in (0, 1]; a ratio of 0 would make the penalty undefined, so it
      // is floored just above zero.
      healthyReciprocityRatio: boundedNumber(
        relationshipRules.healthyReciprocityRatio,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.relationshipRules
          .healthyReciprocityRatio,
        0.01,
        1,
      ),
    },
    deliveryRules: {
      overdueProjectPenalty: numberValue(
        deliveryRules.overdueProjectPenalty,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.deliveryRules
          .overdueProjectPenalty,
      ),
      openTicketPenalty: numberValue(
        deliveryRules.openTicketPenalty,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.deliveryRules.openTicketPenalty,
      ),
      highPriorityTicketPenalty: numberValue(
        deliveryRules.highPriorityTicketPenalty,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.deliveryRules
          .highPriorityTicketPenalty,
      ),
      highPriorityLevels: highPriorityLevelsValue(
        deliveryRules.highPriorityLevels,
      ),
    },
    commercialRules: {
      targetMarginPercent: boundedNumber(
        commercialRules.targetMarginPercent,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.commercialRules
          .targetMarginPercent,
        0,
        100,
      ),
      targetCollectionRatio: boundedNumber(
        commercialRules.targetCollectionRatio,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.commercialRules
          .targetCollectionRatio,
        0,
        1,
      ),
      unpaidInvoicePenalty: numberValue(
        commercialRules.unpaidInvoicePenalty,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.commercialRules
          .unpaidInvoicePenalty,
      ),
      scopeCreepPenaltyCap: nonNegativeNumber(
        commercialRules.scopeCreepPenaltyCap,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.commercialRules
          .scopeCreepPenaltyCap,
      ),
      // Clamped to [0.01, 1]: a ratio of 0 would divide by zero when the ramp is
      // evaluated, so the floor keeps it well-defined; a full-penalty ratio
      // above 1 (100% of contract value) is degenerate.
      scopeCreepFullPenaltyRatio: boundedNumber(
        commercialRules.scopeCreepFullPenaltyRatio,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.commercialRules
          .scopeCreepFullPenaltyRatio,
        0.01,
        1,
      ),
    },
    triageRules: {
      criticalValueAtRisk: nonNegativeNumber(
        triageRules.criticalValueAtRisk,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.triageRules.criticalValueAtRisk,
      ),
      highValueAtRisk: nonNegativeNumber(
        triageRules.highValueAtRisk,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.triageRules.highValueAtRisk,
      ),
      mediumValueAtRisk: nonNegativeNumber(
        triageRules.mediumValueAtRisk,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.triageRules.mediumValueAtRisk,
      ),
      alertValueAtRisk: nonNegativeNumber(
        triageRules.alertValueAtRisk,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.triageRules.alertValueAtRisk,
      ),
      alertRelationshipScore: thresholdValue(
        triageRules.alertRelationshipScore,
        DEFAULT_ENTITY_HEALTH_SCORING_PROFILE.triageRules
          .alertRelationshipScore,
      ),
    },
  }
}

export function normalizeDealScoringProfile(
  value: unknown,
): DealScoringProfile {
  const input = objectValue<DealScoringProfile>(value)
  const thresholds = objectValue<DealScoringProfile['thresholds']>(
    input.thresholds,
  )
  const issueImpacts = objectValue<DealScoringProfile['issueImpacts']>(
    input.issueImpacts,
  )
  const staleDays = objectValue<DealScoringProfile['staleDays']>(
    input.staleDays,
  )
  const probabilitySource =
    input.probabilitySource === 'stage' ||
    input.probabilitySource === 'higher' ||
    input.probabilitySource === 'lower'
      ? input.probabilitySource
      : DEFAULT_DEAL_SCORING_PROFILE.probabilitySource

  return {
    enabled:
      typeof input.enabled === 'boolean'
        ? input.enabled
        : DEFAULT_DEAL_SCORING_PROFILE.enabled,
    thresholds: {
      strong: thresholdValue(
        thresholds.strong,
        DEFAULT_DEAL_SCORING_PROFILE.thresholds.strong,
      ),
      watch: thresholdValue(
        thresholds.watch,
        DEFAULT_DEAL_SCORING_PROFILE.thresholds.watch,
      ),
    },
    issueImpacts: {
      missingOwner: numberValue(
        issueImpacts.missingOwner,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.missingOwner,
      ),
      missingClient: numberValue(
        issueImpacts.missingClient,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.missingClient,
      ),
      missingPrimaryContact: numberValue(
        issueImpacts.missingPrimaryContact,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.missingPrimaryContact,
      ),
      missingAmount: numberValue(
        issueImpacts.missingAmount,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.missingAmount,
      ),
      missingProbability: numberValue(
        issueImpacts.missingProbability,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.missingProbability,
      ),
      pastCloseDate: numberValue(
        issueImpacts.pastCloseDate,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.pastCloseDate,
      ),
      nearCloseOutsideNegotiation: numberValue(
        issueImpacts.nearCloseOutsideNegotiation,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.nearCloseOutsideNegotiation,
      ),
      staleActivity: numberValue(
        issueImpacts.staleActivity,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.staleActivity,
      ),
      quietActivity: numberValue(
        issueImpacts.quietActivity,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.quietActivity,
      ),
      onHold: numberValue(
        issueImpacts.onHold,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.onHold,
      ),
      noRecentClientContact: numberValue(
        issueImpacts.noRecentClientContact,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.noRecentClientContact,
      ),
      singleThreaded: numberValue(
        issueImpacts.singleThreaded,
        DEFAULT_DEAL_SCORING_PROFILE.issueImpacts.singleThreaded,
      ),
    },
    staleDays: {
      quiet: integerValue(
        staleDays.quiet,
        DEFAULT_DEAL_SCORING_PROFILE.staleDays.quiet,
      ),
      stale: integerValue(
        staleDays.stale,
        DEFAULT_DEAL_SCORING_PROFILE.staleDays.stale,
      ),
    },
    nearCloseDays: integerValue(
      input.nearCloseDays,
      DEFAULT_DEAL_SCORING_PROFILE.nearCloseDays,
    ),
    contactSilenceDays: integerValue(
      input.contactSilenceDays,
      DEFAULT_DEAL_SCORING_PROFILE.contactSilenceDays,
    ),
    probabilitySource,
    // Clamp to >= 1: a floor of 0 would "trust" an empty cohort, defeating the
    // cold-start guard entirely.
    derivedProbabilitySampleFloor: Math.max(
      1,
      integerValue(
        input.derivedProbabilitySampleFloor,
        DEFAULT_DEAL_SCORING_PROFILE.derivedProbabilitySampleFloor,
      ),
    ),
    // T10 — only the two valid drivers are accepted; anything else falls back to
    // the default (rep) so a malformed preference never silently flips the
    // headline forecast onto an unintended source.
    headlineProbabilityDriver:
      input.headlineProbabilityDriver === 'derived'
        ? 'derived'
        : DEFAULT_DEAL_SCORING_PROFILE.headlineProbabilityDriver,
  }
}
